import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useLanguage } from "@/hooks/useLanguage";
import { useUserProfile } from "@/lib/pr21/userProfile";
import { useAuth } from "@/hooks/useAuth";
import {
  exportAndShareToWhatsApp,
  PERSONAL_CLIP_LABEL,
  type PersonalClipPayload,
} from "@/lib/clipExport/personalClips";
import { recordViralClip } from "@/lib/viralClips.functions";
import { scoreMoment } from "@/lib/viralMomentEngine";
import { recordWarRoomEvent } from "@/lib/warRoom/warRoomEngine";
import { bumpMission } from "@/lib/ambassadors/ambassadorEngine";

/**
 * Personal/competitive clip exporter button. Generic corporate
 * flyer exports are no longer allowed — every clip must belong to
 * one of the 8 PersonalClipKind categories.
 */
export function ExportClipButton({
  clip,
  label,
}: {
  clip: PersonalClipPayload;
  label?: string;
}) {
  const { t } = useLanguage();
  const profile = useUserProfile();
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const persist = useServerFn(recordViralClip);
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    setBusy(true);
    try {
      const enriched: PersonalClipPayload = {
        ...clip,
        nation: clip.nation ?? profile?.favorite_national_team ?? profile?.favorite_team,
        handle: clip.handle ?? profile?.username ?? profile?.display_name,
      };

      // Real viral score (used for platform routing + persisted)
      const v = scoreMoment({
        kind: enriched.kind,
        isRivalry: enriched.kind === "batalla_naciones",
        hypeIndex: typeof enriched.progress === "number" ? enriched.progress : null,
      });

      // FASE A — persist a real row in public.viral_clips BEFORE exporting.
      // RLS requires auth.uid() = user_id; skip silently for guests so the
      // share still works (image + WhatsApp), just without ranking impact.
      if (isAuthenticated) {
        try {
          const res = await persist({
            data: {
              kind: enriched.kind,
              nation: enriched.nation ?? null,
              rivalNation: enriched.rivalNation ?? null,
              headline: enriched.headline ?? null,
              subline: enriched.subline ?? null,
              position: enriched.position ?? null,
              viralScore: v.viralScore,
              platform: v.recommendedPlatform,
            },
          });
          if (!res.ok) console.warn("[viral_clips] insert failed:", res.error);
          else {
            // FASE B — refresh Top Clips / Top Creadores / Trending / World Pulse.
            qc.invalidateQueries({ queryKey: ["worldPulse"] });
          }
        } catch (e) {
          console.warn("[viral_clips] persist threw:", e);
        }
      }

      // FASE C — share image with WhatsApp-aware fallback.
      await exportAndShareToWhatsApp(enriched);
      recordWarRoomEvent("clipExports");
      bumpMission("share_3_clips");
      toast.success(t("clip_ready"));
    } catch {
      toast.error(t("clip_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={PERSONAL_CLIP_LABEL[clip.kind]}
      className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95 disabled:opacity-60"
    >
      <span aria-hidden className="text-sm leading-none">🔥</span>
      <Download className="h-3.5 w-3.5" />
      {label ?? `Crear ${PERSONAL_CLIP_LABEL[clip.kind]}`}
    </button>
  );
}
